import { showContextualView } from './navigationController';
import { applyIdeaDocumentSeo } from './courseDocumentSeo';
import { renderCommunityIdeaDetail, type CommunityIdeaDetailRenderContext } from './communityIdeaDetailRenderer';
import type { PrototypeApiClient } from './apiClient';
import type { Root } from './prototypeTypes';
import { getRuntime } from './runtimeAccessor';
import { resetTransientSurfaceState } from './transientSurfaceState';

const detailGenerations = new WeakMap<object, number>();
const detailRequests = new WeakMap<object, { generation: number; ideaId: string; pending: boolean; complete: boolean }>();

export interface IdeaSummary {
	id: string;
	title: string;
	subtitle: string | null;
	isAdopted: boolean;
	displayStatus: 'Voting' | 'Selected';
	viewerLiked: boolean;
	strengthenItems: string[];
	weakenItems: string[];
	links: Array<{ url: string; label?: string }>;
	attachments: Array<{ name: string; size: number; mimeType: string }>;
	publicIndexable: boolean;
	available?: boolean;
	navigableTopicSlug: string | null;
}

export function beginIdeaDetailEntry(root: Root, ideaId: string, owner: 'controller' | 'hydrator' = 'controller'): number {
	const view = root.querySelector<HTMLElement>('#view-idea-detail');
	if (view) resetTransientSurfaceState(view);
	const generation = (detailGenerations.get(root as object) ?? 0) + 1;
	detailGenerations.set(root as object, generation);
	detailRequests.set(root as object, { generation, ideaId, pending: true, complete: false });
	if (view) { view.dataset.ideaGeneration = String(generation); view.dataset.ideaLoadOwner = owner; }
	const runtime = getRuntime(root);
	if (runtime) runtime.__activeIdeaSlug = ideaId;
	return generation;
}

export function currentIdeaDetailGeneration(root: Root): number { return detailGenerations.get(root as object) ?? 0; }

export function isCurrentIdeaDetail(root: Root, ideaId: string, generation: number): boolean {
	const view = root.querySelector<HTMLElement>('#view-idea-detail');
	const currentGeneration = detailGenerations.get(root as object);
	const activeIdea = getRuntime(root)?.__activeIdeaSlug;
	return (currentGeneration === undefined || currentGeneration === generation)
		&& (activeIdea === undefined || activeIdea === ideaId)
		&& (!view || view.classList.contains('active'));
}

export function hasPendingIdeaDetailRequest(root: Root, generation: number): boolean {
	const request = detailRequests.get(root as object);
	return request?.generation === generation && request.pending;
}

export function hasCompletedIdeaDetailRequest(root: Root, ideaId: string, generation: number): boolean {
	const request = detailRequests.get(root as object);
	return request?.generation === generation && request.ideaId === ideaId && request.complete;
}

function host(root: Root): HTMLElement | null { return root.querySelector<HTMLElement>('[data-community-detail-host]'); }

function paintState(root: Root, title: string, message: string, retryAction?: string): void {
	const target = host(root);
	if (!target) return;
	target.innerHTML = `<section class="community-detail-state" role="status"><h2 class="community-detail-title">${title}</h2><p>${message}</p>${retryAction ? `<button type="button" class="btn btn-secondary" data-proto-click="${retryAction}" aria-label="Retry loading community request">Retry</button>` : ''}</section>`;
}

function paintIdea(root: Root, idea: IdeaSummary, context: CommunityIdeaDetailRenderContext): void {
	const target = host(root);
	if (target) target.innerHTML = renderCommunityIdeaDetail(idea, context);
}

export function showIdeaDetail(ideaId: string, root: Root): boolean {
	const generation = beginIdeaDetailEntry(root, ideaId);
	showContextualView("idea-detail", root);
	paintState(root, 'Loading idea…', 'Loading this community pick.');
	const runtime = getRuntime(root);
	if (runtime) runtime.__lastVoteAt = undefined;
	void loadAndPaintIdeaDetail(ideaId, root, undefined, () => isCurrentIdeaDetail(root, ideaId, generation));
	return true;
}

export async function loadAndPaintIdeaDetail(ideaId: string, root: Root, apiOverride?: PrototypeApiClient, isCurrent?: () => boolean): Promise<void> {
	const generation = currentIdeaDetailGeneration(root);
	const ownsCurrent = isCurrent ?? (() => isCurrentIdeaDetail(root, ideaId, generation));
	const api = apiOverride ?? getRuntime(root)?.api;
	const request = detailRequests.get(root as object);
	const fetchStartedAt = Date.now();
	try {
		if (!api?.getCommunityIdea) throw new Error('community_detail_api_unavailable');
		const response = await api.getCommunityIdea(ideaId);
		if (!ownsCurrent()) return;
		if ((getRuntime(root)?.__lastVoteAt ?? 0) > fetchStartedAt) return;
		if (!response?.idea) {
			paintState(root, 'Idea not found', 'This community pick is unavailable.');
			return;
		}
		paintIdea(root, response.idea as IdeaSummary, { surface: 'page' });
		applyIdeaDocumentSeo(document, response.idea, ideaId);
	} catch {
		if (ownsCurrent()) paintState(root, 'Could not load idea', 'Please try again.', `retryIdeaDetail('${ideaId}')`);
	} finally {
		if (request?.generation === generation) {
			request.pending = false;
			if (ownsCurrent()) request.complete = true;
		}
	}
}

export function retryIdeaDetail(ideaId: string, root: Root): void {
	const generation = beginIdeaDetailEntry(root, ideaId);
	paintState(root, 'Loading idea…', 'Loading this community pick.');
	void loadAndPaintIdeaDetail(ideaId, root, undefined, () => isCurrentIdeaDetail(root, ideaId, generation));
}
